import React, { useEffect, useRef, useState } from "react";
import { sendChatMessage } from "../../api/chat";
import EmojiPicker, { Theme, EmojiClickData } from "emoji-picker-react";
import { Popover } from "@headlessui/react";
import { usePopper } from "react-popper";
import { GifPicker, Gif } from 'gif-picker-react';
import { Klipy } from "gif-picker-react/providers/klipy";

import { soundKeys } from "../emotes/emoteKeys";

const klipyApiKey = process.env.REACT_APP_TENOR_APIKEY || "";

export function ChatInput() {
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const [messageValue, setMessageValue] = useState<string>("");
  const [emojiButtonElement, setEmojiButtonElement] =
    useState<HTMLButtonElement | null>(null);
  const [emojiPanelElement, setEmojiPanelElement] =
    useState<HTMLDivElement | null>(null);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [emoteOptions, setEmoteOptions] = useState<string[]>([]);
  const [selectedEmoteIndex, setSelectedEmoteIndex] = useState(-1);

  const { styles, attributes } = usePopper(
    emojiButtonElement,
    emojiPanelElement,
  );
  const [gifButtonElement, setGifButtonElement] =
    useState<HTMLButtonElement | null>(null);
  const [gifPanelElement, setGifPanelElement] = useState<HTMLDivElement | null>(
    null,
  );
  const { styles: gifStyles, attributes: gifAttributes } = usePopper(
    gifButtonElement,
    gifPanelElement,
  );

  // Resize the textarea to fit the content
  useEffect(() => {
    if (messageRef.current) {
      messageRef.current.style.height = "auto";
      messageRef.current.style.height = `${
        messageRef.current.scrollHeight - 12
      }px`; // 12px padding
    }
  }, [messageValue]);

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const getEmoteQuery = (text: string) => {
    const match = text.match(/^\/(\w+)/);
    return match ? match[1] : null;
  };

  const updateEmoteOptions = (text: string) => {
    const query = getEmoteQuery(text);

    if (text.startsWith("/")) {
      const filtered = query
        ? soundKeys.filter((emote) =>
            emote.toLowerCase().includes("/" + query.toLowerCase()),
          )
        : soundKeys;
      setEmoteOptions(filtered);
      setSelectedEmoteIndex(-1); // No selection by default
    } else {
      setEmoteOptions([]);
      setSelectedEmoteIndex(-1);
    }
  };

  const handleSubmitMessage = (e: React.FormEvent, message?: string) => {
    const msg = message || messageValue;
    e.preventDefault();
    if (msg && msg.trim() !== "") {
      sendChatMessage(msg.trim());
      // Clear textinput
      setMessageValue("");
      // Reset emote options
      setEmoteOptions([]);
      setSelectedEmoteIndex(-1);
    }
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    if (messageRef.current) {
      setMessageValue(
        (prev) =>
          prev.substr(0, messageRef?.current?.selectionStart) +
          emojiData.emoji +
          prev.substr(messageRef?.current?.selectionEnd || 0),
      );
    }
  };

  const handleMessageKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (emoteOptions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedEmoteIndex((prev) =>
          prev < emoteOptions.length - 1 ? prev + 1 : -1,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedEmoteIndex((prev) =>
          prev === -1 ? emoteOptions.length - 1 : prev - 1,
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (selectedEmoteIndex !== -1) {
          const selectedEmote = emoteOptions[selectedEmoteIndex];
          handleSubmitMessage(e, selectedEmote);
        } else {
          handleSubmitMessage(e);
        }
      }
    } else if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmitMessage(e);
    }
  };

  return (
    <div className="flex items-end">
      <form className="w-full" onSubmit={handleSubmitMessage}>
        <div className="flex items-center px-3 py-1.5 bg-gray-700">
          <Popover className="relative">
            <Popover.Button
              ref={setEmojiButtonElement}
              className="p-2 rounded-lg cursor-pointer text-gray-400 hover:text-white resize-none hover:bg-gray-600"
            >
              <svg
                className="w-5 h-5"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 20"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13.408 7.5h.01m-6.876 0h.01M19 10a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM4.6 11a5.5 5.5 0 0 0 10.81 0H4.6Z"
                />
              </svg>
              <span className="sr-only">Add emoji</span>
            </Popover.Button>

            <Popover.Panel
              ref={setEmojiPanelElement}
              className="absolute z-10"
              style={styles.popper}
              {...attributes.popper}
            >
              <EmojiPicker theme={Theme.DARK} onEmojiClick={handleEmojiClick} />
            </Popover.Panel>
          </Popover>
          {klipyApiKey !== "" && (
            <Popover className="relative">
              <Popover.Button
                ref={setGifButtonElement}
                className="p-2 rounded-lg cursor-pointer  text-gray-400 hover:text-white resize-none hover:bg-gray-600"
              >
                GIF
                <span className="sr-only">Add GIF</span>
              </Popover.Button>
              <Popover.Panel
                ref={setGifPanelElement}
                className="absolute z-10"
                style={gifStyles.popper}
                {...gifAttributes.popper}
              >
                {({ close }) => (
                  <GifPicker
                    provider={Klipy(klipyApiKey)}
                    theme={Theme.DARK}
                    onGifClick={(gif: Gif) => {
                      if (messageRef.current) {
                        const width = 200;
                        // use gif.width and gif.height to calculate aspect ratio and set the new height to match the new width
                        const height = (gif.height / gif.width) * width;
                        sendChatMessage(
                          `<img src="${gif.imageUrl}" width="${width}px" height="${height}px" />`,
                        );
                      }
                      close();
                    }}
                  />
                )}
              </Popover.Panel>
            </Popover>
          )}
          <div className="relative w-full pr-6">
            {emoteOptions.length > 0 && (
              <div
                className="absolute bottom-12 left-4 bg-slate-600 text-white rounded shadow p-1 w-48 z-50 text-xs overflow-y-auto max-w-full"
                style={{
                  maxHeight: windowHeight - 150 + "px",
                }}
              >
                {emoteOptions.map((emote, idx) => (
                  <div
                    key={emote}
                    className={`px-1.5 overflow-ellipsis overflow-x-hidden max-w-full py-1.5 cursor-pointer rounded-sm hover:bg-slate-500/50 ${
                      idx === selectedEmoteIndex ? "bg-slate-500" : ""
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSubmitMessage(e, emote);
                    }}
                  >
                    {emote}
                  </div>
                ))}
              </div>
            )}
            <textarea
              cols={1}
              ref={messageRef}
              value={messageValue}
              onChange={(e) => {
                const value = e.target.value;
                setMessageValue(value);
                updateEmoteOptions(value);
              }}
              onKeyDown={handleMessageKeyDown}
              className="block mx-4 pt-2.5 pb-1 w-full text-sm rounded-lg border bg-gray-800 border-gray-600 placeholder-gray-400 text-white focus:ring-sky-500 focus:border-sky-500 resize-none"
              placeholder="Your message..."
              style={{
                overflow: "hidden",
                height: "40px",
                maxHeight: windowHeight / 2,
              }}
            ></textarea>
          </div>
          <button
            type="submit"
            className="inline-flex justify-center p-2 rounded-full cursor-pointer text-sky-500 hover:bg-gray-600"
          >
            <svg
              className="w-5 h-5 rotate-90"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 18 20"
            >
              <path d="m17.914 18.594-8-18a1 1 0 0 0-1.828 0l-8 18a1 1 0 0 0 1.157 1.376L8 18.281V9a1 1 0 0 1 2 0v9.281l6.758 1.689a1 1 0 0 0 1.156-1.376Z" />
            </svg>
            <span className="sr-only">Send message</span>
          </button>
        </div>
      </form>
    </div>
  );
}
